// Smart Account Service - Backend API integration for Account Abstraction
const API_URL = process.env.REACT_APP_API_URL || 'https://tscm-backend.vercel.app';

interface SmartAccountInfo {
  address: string | null;
  eoaAddress: string;
  isDeployed: boolean;
}

interface TransactionResult {
  success: boolean;
  userOpHash?: string;
  transactionHash?: string;
  tokenId?: string;
  error?: string;
}

interface TransactionRecord {
  id: string;
  operationType: string;
  status: string;
  transactionHash?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Create or get user's smart account
export const getOrCreateSmartAccount = async (): Promise<SmartAccountInfo> => {
  const response = await fetch(`${API_URL}/api/smart-account/account`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to create smart account');
  }

  return data.smartAccount;
};

// Mint certificate via sponsored transaction
export const mintCertificateSponsored = async (
  craftsmanName: string,
  name: string,
  description: string,
  imageIPFSHash: string,
  metadataIPFSHash: string,
  metadata?: Record<string, unknown>
): Promise<TransactionResult> => {
  const response = await fetch(`${API_URL}/api/smart-account/mint-certificate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      craftsmanName,
      name,
      description,
      imageIPFSHash,
      metadataIPFSHash,
      metadata,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: [data.error || 'Failed to mint certificate', data.details].filter(Boolean).join(' — '),
    };
  }

  return {
    success: true,
    userOpHash: data.transaction?.userOpHash,
    transactionHash: data.transaction?.transactionHash,
    tokenId: data.transaction?.tokenId,
  };
};

// Update item status via sponsored transaction
export const updateStatusSponsored = async (
  tokenId: string,
  statusEnumValue: number,
  notes: string,
  latitude: string,
  longitude: string
): Promise<TransactionResult> => {
  const response = await fetch(`${API_URL}/api/smart-account/update-status`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      tokenId,
      statusEnumValue,
      notes,
      latitude,
      longitude,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: [data.error || 'Failed to update status', data.details].filter(Boolean).join(' — '),
    };
  }

  return {
    success: true,
    userOpHash: data.transaction?.userOpHash,
    transactionHash: data.transaction?.transactionHash,
  };
};

// Update certificate via sponsored transaction
export const updateCertificateSponsored = async (
  tokenId: string,
  newMetadataIPFSHash: string
): Promise<TransactionResult> => {
  const response = await fetch(`${API_URL}/api/smart-account/update-certificate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      tokenId,
      newMetadataIPFSHash,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: [data.error || 'Failed to update certificate', data.details].filter(Boolean).join(' — '),
    };
  }

  return {
    success: true,
    userOpHash: data.transaction?.userOpHash,
    transactionHash: data.transaction?.transactionHash,
  };
};

// Get user's transaction history
export const getTransactionHistory = async (): Promise<{
  transactions: TransactionRecord[];
  stats: { total: number; confirmed: number; failed: number; pending: number };
}> => {
  const response = await fetch(`${API_URL}/api/smart-account/transactions`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch transactions');
  }

  return {
    transactions: data.transactions,
    stats: data.stats,
  };
};

// Check if user has a smart account
export const hasSmartAccount = async (): Promise<boolean> => {
  try {
    const account = await getOrCreateSmartAccount();
    return !!account.address || !!account.eoaAddress;
  } catch {
    return false;
  }
};
